import React from 'react';
import PropTypes from 'prop-types';
import { getBaseButtonClasses, getFocusRingClasses, wrapWithLoadingSpinner, getIconSpacingClasses } from './buttonUtils';

/**
 * DangerButton Component
 * 
 * A destructive action button with red styling.
 * Used for dangerous operations like delete, remove, or irreversible actions.
 * 
 * @example
 * // Basic danger button
 * <DangerButton onClick={handleDelete}>
 *   Delete Website
 * </DangerButton>
 * 
 * @example
 * // With icon and loading
 * <DangerButton
 *   leftIcon={<TrashIcon />}
 *   loading={isDeleting}
 *   onClick={handleDelete}
 * >
 *   Delete
 * </DangerButton>
 * 
 * @example
 * // Outline variant (less aggressive)
 * <DangerButton variant="outline" onClick={handleRemove}>
 *   Remove
 * </DangerButton>
 */
const DangerButton = ({
  children,
  onClick,
  type = 'button',
  variant = 'solid',
  size = 'md',
  disabled = false,
  loading = false,
  fullWidth = false,
  leftIcon = null,
  rightIcon = null,
  className = '',
  ...rest
}) => {
  const baseClasses = getBaseButtonClasses({ size, disabled, fullWidth, loading });
  const focusRingClasses = getFocusRingClasses('danger');

  // Variant-specific classes
  const variantClasses = {
    solid: [
      'bg-red-600',
      'hover:bg-red-700',
      'text-white',
      'shadow-sm',
      'hover:shadow-md',
    ],
    outline: [
      'bg-white',
      'hover:bg-red-50',
      'text-red-600',
      'border',
      'border-red-300',
      'hover:border-red-400',
      // Dark theme: avoid bright white fill while keeping a clear destructive affordance
      'dark:bg-dark-900',
      'dark:hover:bg-red-900/20',
      'dark:text-red-300',
      'dark:border-red-800',
      'dark:hover:border-red-700',
    ],
  };

  const classes = [
    baseClasses,
    ...(variantClasses[variant] || variantClasses.solid),
    focusRingClasses,
    className,
  ].join(' ');

  const buttonContent = (
    <>
      {!loading && leftIcon && (
        <span className={getIconSpacingClasses('left', !!children)}>
          {leftIcon}
        </span>
      )}
      {wrapWithLoadingSpinner(loading, children, size)}
      {!loading && rightIcon && (
        <span className={getIconSpacingClasses('right', !!children)}>
          {rightIcon}
        </span>
      )}
    </>
  );

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={classes}
      aria-busy={loading}
      {...rest}
    >
      {buttonContent}
    </button>
  );
};

DangerButton.propTypes = {
  /** Button content */
  children: PropTypes.node,
  /** Click handler */
  onClick: PropTypes.func,
  /** Button type */
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  /** Visual variant */
  variant: PropTypes.oneOf(['solid', 'outline']),
  /** Button size */
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  /** Disabled state */
  disabled: PropTypes.bool,
  /** Loading state */
  loading: PropTypes.bool,
  /** Full width button */
  fullWidth: PropTypes.bool,
  /** Icon to display on the left */
  leftIcon: PropTypes.node,
  /** Icon to display on the right */
  rightIcon: PropTypes.node,
  /** Additional CSS classes */
  className: PropTypes.string,
};

export default DangerButton;
